import type { AIEvent } from '@doger/core/shared/api';

export class RunAgentResponseStreamParser {
  /**
   * @see https://github.com/google/adk-web/blob/d4d05028750712cff682d26fcdf516ee3541f905/src/app/core/services/agent.service.ts#L66
   */
  public async parse(response: Response) {
    try {
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      let lastData = '';

      await new Promise<void>((resolve) => {
        const read = () => {
          reader!
            .read()
            .then(({ done, value }) => {
              if (done) {
                return resolve();
              }
              const chunk = decoder.decode(value, { stream: true });
              lastData += chunk;

              try {
                lastData
                  .split(/\r?\n/)
                  .filter((line) => line.startsWith('data:'))
                  .forEach((line) => {
                    const data = line.replace(/^data:\s*/, '');

                    const json = JSON.parse(data);

                    this.emitChunk(json as AIEvent);
                  });
                lastData = '';
              } catch (e) {
                // the data is not a valid json, it could be an incomplete
                // chunk. we ignore it and wait for the next chunk.
                if (e instanceof SyntaxError) {
                  read();
                }
              }

              read(); // Read the next chunk
            })
            .catch((err) => {
              this.emitError(err);
            });
        };

        read();
      });
    } catch (e) {
      this.emitError(e as Error);
    }
  }

  public onChunk(fn: (chunk: AIEvent) => void): this {
    this.listeners.push({ type: 'chunk', fn });

    return this;
  }

  public onError(fn: (error: Error) => void): this {
    this.listeners.push({ type: 'error', fn });

    return this;
  }

  private readonly listeners: (
    | {
        type: 'chunk';
        fn: (chunk: AIEvent) => void;
      }
    | {
        type: 'error';
        fn: (error: Error) => void;
      }
  )[] = [];

  private emitChunk(chunk: AIEvent) {
    this.listeners.forEach((listener) => {
      if (listener.type === 'chunk') {
        listener.fn(chunk);
      }
    });
  }

  private emitError(error: Error) {
    this.listeners.forEach((listener) => {
      if (listener.type === 'error') {
        listener.fn(error);
      }
    });
  }
}
